// Reusable offline mutation queue (R-145). Generalises the Logistics POD/
// checkpoint retry queue (domains/logistics) into ONE app-wide FIFO that any
// idempotent write can ride: a network failure lands the request in a
// localStorage queue and it flushes on reconnect (`online` event) + a poll.
//
// Drop rules (the server is authoritative):
//   • 2xx            → applied, drop.
//   • 409 Conflict   → already applied (an offline replay of a client_ref-keyed
//                      write, or a re-completion) — success for our idempotent
//                      endpoints, drop.
//   • other 4xx      → a validation rejection the operator must fix; PARK the
//                      item (kept, flagged, never auto-retried) so a bad entry
//                      can't wedge the queue behind it.
//   • network / 5xx  → the request never reached the authoritative server; stop
//                      the flush and retry later (order preserved).
//
// The Workbox service worker is untouched — this is an application-tier queue
// for authenticated JSON mutations, orthogonal to asset precaching.
import { useEffect, useState } from 'preact/hooks';
import { newClientRef } from './core/client-ref';
import { useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from './core/client';
import { resolvePath } from './core/path';
import { useConfigStore } from '@xantory/store';

const QUEUE_KEY = 'xantory:offline-queue';
const CHANGED_EVENT = 'xantory-offline-queue-changed';

export interface QueuedMutation {
  /** Queue-entry id (distinct from any domain idempotency key). */
  id: string;
  method: 'POST' | 'PUT';
  /** The API path this item will be replayed to, **already resolved to its
   *  tier and site** (see `enqueue`) — NOT the logical path. In local mode the
   *  two are identical; in cloud mode this carries the `/sites/{id}` prefix that
   *  pins the write to the farm it was made for. */
  path: string;
  /** JSON body — includes the domain `client_ref` when the endpoint keys on it. */
  body?: unknown;
  /** react-query key prefix invalidated after this item flushes. */
  invalidate?: readonly unknown[];
  /** Short human label for the pending/parked badge. */
  label?: string;
  queued_at: number;
  /** Server rejected with a non-409 4xx — needs a human, never auto-retried. */
  parked?: boolean;
  /** The parked rejection message. */
  error?: string;
}

function readQueue(): QueuedMutation[] {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) ?? '[]') as QueuedMutation[];
  } catch {
    return [];
  }
}

function writeQueue(q: QueuedMutation[]) {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
  window.dispatchEvent(new Event(CHANGED_EVENT));
}

/** Queueable failure = the request never reached the authoritative server (a
 *  fetch-level error or the proxy's 502/503/504). Real 4xx answers are server
 *  decisions and are never treated as network errors. */
function isNetworkError(e: unknown): boolean {
  if (!(e instanceof ApiError)) return true;
  return e.status === 502 || e.status === 503 || e.status === 504;
}

function enqueue(item: Omit<QueuedMutation, 'id' | 'queued_at' | 'parked' | 'error'>): QueuedMutation {
  const entry: QueuedMutation = {
    ...item,
    // ⚠⚠ PIN THE DESTINATION NOW, not on replay. The queue OUTLIVES the site
    // picker: `resolvePath` reads whichever site is selected at call time, so a
    // write queued for one farm and flushed after switching farms would land on
    // the OTHER farm. For receiving that means booking goods into a site that
    // never received them — minting assets and consuming that site's label pool.
    //
    // Resolving here freezes the farm at the moment the operator acted. Safe to
    // do twice: an already-resolved `/sites/{id}/…` path matches none of
    // `resolvePath`'s rewrite branches and passes straight through, so replay
    // through the normal client is unchanged.
    path: resolvePath(item.path),
    id: newClientRef(),
    queued_at: Date.now(),
  };
  writeQueue([...readQueue(), entry]);
  return entry;
}

/** A cloud-mode entry queued BEFORE destinations were pinned: it still holds a
 *  logical `/v1/…` path, so replaying it would resolve against whatever site is
 *  selected now. There is no way to recover which farm it was meant for, and
 *  guessing is the exact failure being fixed — so it is parked for a human. */
function isUnpinned(item: QueuedMutation): boolean {
  return useConfigStore.getState().mode === 'cloud' && item.path.startsWith('/v1/');
}

async function replay(item: QueuedMutation): Promise<void> {
  if (item.method === 'PUT') await api.put(item.path, item.body);
  else await api.post(item.path, item.body);
}

/**
 * The reusable primitive: try the write online; on a network failure enqueue it
 * (returning null so the mutation still resolves and the UI proceeds), and
 * rethrow any genuine server rejection so the caller surfaces it.
 */
export async function submitQueueable<T>(
  item: Omit<QueuedMutation, 'id' | 'queued_at' | 'parked' | 'error'>,
): Promise<T | null> {
  try {
    return item.method === 'PUT'
      ? await api.put<T>(item.path, item.body)
      : await api.post<T>(item.path, item.body);
  } catch (e) {
    if (!isNetworkError(e)) throw e;
    enqueue(item);
    return null;
  }
}

/** The result of a flush pass: how many drained + the keys to invalidate. */
export interface FlushResult {
  flushed: number;
  invalidate: (readonly unknown[])[];
}

/** Flush front-to-back. Parked items are skipped; a network failure stops the
 *  pass (order preserved); a non-409 4xx parks the head and moves on. */
export async function flushOfflineQueue(): Promise<FlushResult> {
  let queue = readQueue();
  let flushed = 0;
  const invalidate: (readonly unknown[])[] = [];
  let i = 0;
  while (i < queue.length) {
    const head = queue[i]!;
    if (head.parked) {
      i += 1;
      continue;
    }
    // A pre-pinning entry cannot be replayed safely — see `isUnpinned`. Park it
    // rather than send a farm's goods receipt to whichever farm is selected now.
    if (isUnpinned(head)) {
      queue = queue.map((q) =>
        q.id === head.id
          ? { ...q, parked: true, error: 'Queued before this release and not tied to a site — re-enter it' }
          : q,
      );
      writeQueue(queue);
      i += 1;
      continue;
    }
    try {
      await replay(head);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        // already applied — fall through to drop
      } else if (isNetworkError(e)) {
        break;
      } else if (e instanceof ApiError && e.status >= 400 && e.status < 500) {
        queue = queue.map((q, idx) => (idx === i ? { ...q, parked: true, error: e.message } : q));
        writeQueue(queue);
        i += 1;
        continue;
      } else {
        break;
      }
    }
    if (head.invalidate) invalidate.push(head.invalidate);
    flushed += 1;
    queue = [...queue.slice(0, i), ...queue.slice(i + 1)];
    writeQueue(queue);
  }
  return { flushed, invalidate };
}

/** Drop a single entry (e.g. a parked item the operator dismisses). */
export function discardQueued(id: string) {
  writeQueue(readQueue().filter((q) => q.id !== id));
}

/** The live queue — drives an offline/sync badge. Includes parked entries. */
export function useOfflineQueue(): QueuedMutation[] {
  const [queue, setQueue] = useState<QueuedMutation[]>(readQueue);
  useEffect(() => {
    const update = () => setQueue(readQueue());
    window.addEventListener(CHANGED_EVENT, update);
    window.addEventListener('storage', update);
    return () => {
      window.removeEventListener(CHANGED_EVENT, update);
      window.removeEventListener('storage', update);
    };
  }, []);
  return queue;
}

/** Live `navigator.onLine` — drives the offline hints on online-only actions
 *  (transitions/uploads stay disabled offline; queueable writes keep working). */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}

/** Reconnect → flush → invalidate the flushed items' keys. Mount once, app-wide
 *  (the Shell) — the queue is shared across every domain. */
export function useOfflineQueueSync() {
  const qc = useQueryClient();
  useEffect(() => {
    const flush = () => {
      if (readQueue().some((q) => !q.parked) === false) return;
      void flushOfflineQueue().then(({ flushed, invalidate }) => {
        if (flushed === 0) return;
        const seen = new Set<string>();
        for (const key of invalidate) {
          const sig = JSON.stringify(key);
          if (seen.has(sig)) continue;
          seen.add(sig);
          void qc.invalidateQueries({ queryKey: key });
        }
      });
    };
    window.addEventListener('online', flush);
    const retry = window.setInterval(flush, 30_000);
    flush();
    return () => {
      window.removeEventListener('online', flush);
      window.clearInterval(retry);
    };
  }, [qc]);
}
